import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, qs } from '../lib/api'
import { useAuth } from '../lib/auth'
import { count, dateOnly, money } from '../lib/format'
import type { ListResponse, Tenant } from '../lib/types'
import { Page } from '../components/Shell'
import { Icon } from '../components/Icon'
import { DataTable, Toolbar, useSearch, type Column } from '../components/DataTable'
import { Button, Chip, Field, Modal, Panel, toast } from '../components/ui'

/**
 * Account management: Tier 1 sees resellers, Tier 2 sees its clients. The
 * same screen serves both because the hierarchy is uniform — RLS decides what
 * is in the list, not the UI.
 */
export function Tenants() {
  const { can, user } = useAuth()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const search = useSearch()
  const [offset, setOffset] = useState(0)
  const [creating, setCreating] = useState(false)
  const [suspending, setSuspending] = useState<Tenant | null>(null)
  const [editing, setEditing] = useState<Tenant | null>(null)
  const limit = 50

  const childKind = user?.tier === 1 ? 'reseller' : 'client'
  const canManage = can(childKind === 'reseller' ? 'platform.reseller.manage' : 'client.manage')

  const list = useQuery({
    queryKey: ['tenants', search.debounced, offset],
    queryFn: () =>
      api<ListResponse<Tenant>>(`/tenants${qs({ q: search.debounced, limit, offset })}`),
  })

  const impersonateMut = useImpersonate()

  const columns: Column<Tenant>[] = [
    {
      key: 'name',
      header: 'Account',
      sort: 'name',
      render: (t) => (
        <div className="min-w-0">
          <div className="text-[var(--color-ink)] truncate">{t.name}</div>
          <div className="text-[11px] text-[var(--color-ink-4)] truncate">
            {t.slug} · {t.contactEmail ?? 'no contact'}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sort: 'status',
      width: '110px',
      render: (t) =>
        t.status === 'active' ? (
          <Chip tone="ok">Active</Chip>
        ) : (
          <Chip tone={t.status === 'suspended' ? 'warn' : 'critical'} title={t.suspendReason}>
            {t.status}
          </Chip>
        ),
    },
    { key: 'vehicles', header: 'Vehicles', numeric: true, render: (t) => count(t.vehicleCount) },
    { key: 'devices', header: 'Devices', numeric: true, render: (t) => count(t.deviceCount) },
    { key: 'users', header: 'Users', numeric: true, render: (t) => count(t.userCount) },
    ...(childKind === 'reseller'
      ? [
          {
            key: 'clients',
            header: 'Clients',
            numeric: true,
            render: (t: Tenant) => count(t.clientCount),
          },
        ]
      : []),
    {
      key: 'credit',
      header: 'Credit limit',
      numeric: true,
      render: (t) => (t.creditLimitCents ? money(t.creditLimitCents, t.currency) : '—'),
    },
    {
      key: 'retention',
      header: 'Retention',
      numeric: true,
      width: '90px',
      render: (t) =>
        t.retentionDays ? (
          `${t.retentionDays}d`
        ) : (
          <span className="text-[var(--color-ink-4)]" title="Inherited from the parent account">
            inherit
          </span>
        ),
    },
    { key: 'created', header: 'Created', numeric: true, render: (t) => dateOnly(t.createdAt) },
    {
      key: 'actions',
      header: '',
      width: '110px',
      render: (t) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {canManage && (
            <button
              className="btn btn-ghost btn-sm px-1.5"
              title="Edit account details"
              onClick={() => setEditing(t)}
            >
              <Icon name="edit" size={13} />
            </button>
          )}
          {can('tenant.impersonate') && t.status === 'active' && (
            <button
              className="btn btn-ghost btn-sm px-1.5"
              title="Log in as this account"
              onClick={() => impersonateMut.open(t)}
            >
              <Icon name="external" size={13} />
            </button>
          )}
          {can('tenant.suspend') && (
            <button
              className="btn btn-ghost btn-sm px-1.5"
              title={t.status === 'active' ? 'Suspend' : 'Restore'}
              style={{ color: t.status === 'active' ? 'var(--text-warn)' : 'var(--text-ok)' }}
              onClick={() => setSuspending(t)}
            >
              <Icon name="lock" size={13} />
            </button>
          )}
        </div>
      ),
    },
  ]

  return (
    <Page
      title={childKind === 'reseller' ? 'Resellers' : 'Client accounts'}
      subtitle={
        childKind === 'reseller'
          ? 'Partners who resell the platform under their own brand'
          : 'The companies whose fleets you manage'
      }
      actions={
        can(childKind === 'reseller' ? 'platform.reseller.manage' : 'client.manage') && (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Icon name="plus" size={13} />
            New {childKind}
          </Button>
        )
      }
    >
      <Toolbar search={search.value} onSearch={search.setValue} placeholder="Account name…" />

      <Panel padded={false}>
        <DataTable
          columns={columns}
          rows={list.data?.items}
          rowKey={(t) => t.id}
          loading={list.isLoading}
          error={list.error}
          onRetry={() => void list.refetch()}
          onRowClick={(t) => navigate(`/tenants/${t.id}`)}
          total={list.data?.total}
          limit={limit}
          offset={offset}
          onPage={setOffset}
          empty={{
            title: `No ${childKind}s yet`,
            body:
              childKind === 'reseller'
                ? 'Create a reseller to give a partner their own branded portal and stock pool.'
                : 'Create a client account to start managing a customer fleet.',
          }}
        />
      </Panel>

      {creating && (
        <TenantForm
          kind={childKind}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void qc.invalidateQueries({ queryKey: ['tenants'] })
          }}
        />
      )}

      {editing && (
        <TenantForm
          kind={childKind}
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void qc.invalidateQueries({ queryKey: ['tenants'] })
          }}
        />
      )}

      {suspending && (
        <SuspendModal
          tenant={suspending}
          onClose={() => setSuspending(null)}
          onDone={() => {
            setSuspending(null)
            void qc.invalidateQueries({ queryKey: ['tenants'] })
          }}
        />
      )}

      {impersonateMut.target && (
        <ImpersonateModal target={impersonateMut.target} onClose={impersonateMut.close} />
      )}
    </Page>
  )
}

/* ===========================================================================
   Provisioning
   =========================================================================== */

function TenantForm({
  kind,
  tenant,
  onClose,
  onSaved,
}: {
  kind: 'reseller' | 'client'
  /** Set to edit an existing account; the same form, minus the first administrator. */
  tenant?: Tenant | null
  onClose: () => void
  onSaved: () => void
}) {
  const editing = Boolean(tenant)
  const [form, setForm] = useState({
    name: tenant?.name ?? '',
    slug: tenant?.slug ?? '',
    contactEmail: tenant?.contactEmail ?? '',
    contactPhone: tenant?.contactPhone ?? '',
    timezone: tenant?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    currency: tenant?.currency ?? 'EUR',
    creditLimit: tenant ? (tenant.creditLimitCents ? String(tenant.creditLimitCents / 100) : '') : '',
    retentionDays: tenant?.retentionDays ? String(tenant.retentionDays) : '',
    adminEmail: '',
    adminName: '',
    adminPassword: '',
  })

  const create = useMutation({
    mutationFn: () => {
      const common = {
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim(),
        timezone: form.timezone,
        currency: form.currency,
        creditLimitCents: form.creditLimit ? Math.round(Number(form.creditLimit) * 100) : 0,
      }
      if (tenant) {
        // Retention is only sent when given: the server keeps the current
        // value for an omitted field, and a client admin may not send it.
        return api<unknown>(`/tenants/${tenant.id}`, {
          method: 'PATCH',
          body: { ...common, ...(form.retentionDays ? { retentionDays: Number(form.retentionDays) } : {}) },
        }).then(() => ({ name: common.name }))
      }
      return api<Tenant>('/tenants', {
        body: {
          kind,
          ...common,
          slug: form.slug.trim() || undefined,
          retentionDays: form.retentionDays ? Number(form.retentionDays) : null,
          adminEmail: form.adminEmail.trim(),
          adminName: form.adminName.trim() || form.adminEmail.trim(),
          adminPassword: form.adminPassword,
        },
      })
    },
    onSuccess: (t) => {
      toast(editing ? `${t.name} updated` : `${t.name} created`, 'ok')
      onSaved()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  const set = (k: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  const passwordTooShort = form.adminPassword.length > 0 && form.adminPassword.length < 12

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${tenant!.name}` : `New ${kind} account`}
      width={620}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || passwordTooShort || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? (editing ? 'Saving…' : 'Creating…') : editing ? 'Save changes' : 'Create account'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3.5">
          <div className="sm:col-span-2">
            <Field label="Company name" required>
              <input className="field" value={form.name} onChange={set('name')} autoFocus />
            </Field>
          </div>

          <Field
            label="Identifier"
            hint={editing ? 'Used in URLs; cannot be changed after creation' : 'Used in URLs; generated from the name if left blank'}
          >
            <input
              className="field"
              value={form.slug}
              onChange={set('slug')}
              placeholder="acme-logistics"
              disabled={editing}
            />
          </Field>
          <Field label="Contact email">
            <input className="field" type="email" value={form.contactEmail} onChange={set('contactEmail')} />
          </Field>
          <Field label="Contact phone">
            <input className="field tnum" value={form.contactPhone} onChange={set('contactPhone')} />
          </Field>

          <Field label="Timezone" hint="Report schedules and alert windows use this">
            <input className="field" value={form.timezone} onChange={set('timezone')} />
          </Field>
          <Field label="Currency">
            <select className="field" value={form.currency} onChange={set('currency')}>
              {['EUR', 'USD', 'GBP', 'ZAR', 'AUD', 'CAD'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Credit limit" hint="Major units; 0 for none">
            <input className="field tnum" type="number" value={form.creditLimit} onChange={set('creditLimit')} />
          </Field>
          <Field
            label="Raw data retention"
            hint={
              editing
                ? 'Days of position history. Blank keeps the current setting.'
                : 'Days of position history. Blank inherits from your account.'
            }
          >
            <input
              className="field tnum"
              type="number"
              value={form.retentionDays}
              onChange={set('retentionDays')}
              placeholder="90"
            />
          </Field>
        </div>

        {!editing && (
        <div className="rule-t pt-4">
          <div className="label mb-1">First administrator</div>
          <p className="text-[12px] text-[var(--color-ink-3)] mb-3.5 leading-relaxed">
            Optional, but without one nobody can sign in to the new account until you add a user.
          </p>

          <div className="grid sm:grid-cols-2 gap-3.5">
            <Field label="Email">
              <input className="field" type="email" value={form.adminEmail} onChange={set('adminEmail')} />
            </Field>
            <Field label="Full name">
              <input className="field" value={form.adminName} onChange={set('adminName')} />
            </Field>
            <div className="sm:col-span-2">
              <Field
                label="Initial password"
                hint="At least 12 characters. They should change it on first sign-in."
                error={passwordTooShort ? 'Too short — 12 characters minimum' : undefined}
              >
                <input
                  className="field"
                  type="text"
                  value={form.adminPassword}
                  onChange={set('adminPassword')}
                  autoComplete="new-password"
                />
              </Field>
            </div>
          </div>
        </div>
        )}
      </div>
    </Modal>
  )
}

function SuspendModal({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant
  onClose: () => void
  onDone: () => void
}) {
  const suspend = tenant.status === 'active'
  const [reason, setReason] = useState('')

  const mut = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant.id}/status`, {
        body: { status: suspend ? 'suspended' : 'active', reason: reason.trim() },
      }),
    onSuccess: () => {
      toast(suspend ? `${tenant.name} suspended` : `${tenant.name} restored`, 'ok')
      onDone()
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={suspend ? `Suspend ${tenant.name}` : `Restore ${tenant.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant={suspend ? 'danger' : 'primary'}
            disabled={mut.isPending}
            onClick={() => mut.mutate()}
          >
            {mut.isPending ? 'Working…' : suspend ? 'Suspend account' : 'Restore access'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        {suspend ? (
          <>
            <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
              Everyone at <strong>{tenant.name}</strong> loses access immediately, and this cascades
              to every account beneath it.
            </p>
            <div
              className="px-3 py-2.5 rounded-[3px] text-[12px] leading-relaxed"
              style={{
                background: 'color-mix(in srgb, var(--accent) 8%, transparent)',
                border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)',
              }}
            >
              Telemetry keeps being recorded while suspended. Nothing is lost — restoring access
              gives them a complete history rather than one with a gap in it.
            </div>
          </>
        ) : (
          <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
            Access is restored for <strong>{tenant.name}</strong> and every account beneath it.
            {tenant.suspendReason && (
              <>
                {' '}
                It was suspended for: <em>{tenant.suspendReason}</em>.
              </>
            )}
          </p>
        )}

        {suspend && (
          <Field label="Reason" hint="Shown to the account and recorded in the audit log">
            <input
              className="field"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Invoice 2026-000123 unpaid for 30 days"
              autoFocus
            />
          </Field>
        )}
      </div>
    </Modal>
  )
}

/* ===========================================================================
   Impersonation
   =========================================================================== */

function useImpersonate() {
  const [target, setTarget] = useState<Tenant | null>(null)
  return { target, open: setTarget, close: () => setTarget(null) }
}

function ImpersonateModal({ target, onClose }: { target: Tenant; onClose: () => void }) {
  const { impersonate } = useAuth()
  const navigate = useNavigate()
  const [reason, setReason] = useState('')

  const start = useMutation({
    mutationFn: () => impersonate(target.id, reason.trim()),
    onSuccess: () => {
      toast(`Support session started as ${target.name}`, 'ok')
      onClose()
      navigate('/')
    },
    onError: (e: Error) => toast(e.message, 'error'),
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Log in as ${target.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!reason.trim() || start.isPending} onClick={() => start.mutate()}>
            {start.isPending ? 'Starting…' : 'Start support session'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <p className="text-[13px] text-[var(--color-ink-2)] leading-relaxed">
          You will see <strong>{target.name}</strong> exactly as their own users do, without needing
          their password.
        </p>

        <div
          className="px-3 py-2.5 rounded-[3px] text-[12px] leading-relaxed"
          style={{
            color: 'var(--text-warn)',
            background: 'color-mix(in srgb, var(--color-warn) 9%, transparent)',
            border: '1px solid color-mix(in srgb, var(--color-warn) 28%, transparent)',
          }}
        >
          Everything you do is logged against their account, with your name on it. They can see that
          you were here, and why.
        </div>

        <Field label="Reason" required hint="Visible to the customer in their audit log">
          <input
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ticket #4821 — geofence alerts not firing"
            autoFocus
          />
        </Field>
      </div>
    </Modal>
  )
}
